<!--
 * @Author: Tahiti
 * @Date: 2025-12-17
 * @Description: 特效卡片组件（鼠标移入显示 Use This Template 按钮）
 * @FilePath: /weryai-web/components/Effects/EffectsCard.vue
-->
<template>
    <div
        class="effects-card relative overflow-hidden rounded-xl cursor-pointer group"
        :style="cardStyle"
        @click="handleClick"
        @mouseenter="playPreview"
        @mouseleave="pausePreview"
    >
        <!-- 视频：加载完成后显示，鼠标移入播放 -->
        <video
            v-if="item.video"
            ref="videoRef"
            class="absolute inset-0 size-full object-cover transition-transform duration-300 group-hover:scale-110"
            :poster="posterSrc"
            :src="item.video"
            loop
            muted
            playsinline
            webkit-playsinline
            preload="metadata"
            @loadedmetadata="onMediaLoad"
        ></video>
        <!-- 兜底：如果没有视频，只展示封面图 -->
        <img
            v-else-if="item.cover"
            :src="item.cover"
            :alt="item.title"
            loading="lazy"
            decoding="async"
            class="absolute inset-0 size-full object-cover transition-transform duration-300 group-hover:scale-110"
            @load="onMediaLoad"
        />

        <!-- 热度/点赞数 -->
        <div
            v-if="item.likes"
            class="absolute top-3 right-3 z-[2] flex items-center justify-center gap-1 text-white text-xs rounded-md"
            style="width: 58px; height: 32px; background: rgba(0, 0, 0, 0.6)"
        >
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
            >
                <path
                    d="M12.1108 4.03674C10.2908 4.58491 9.95763 6.13877 10.075 7.12569C8.77669 5.51397 8.83 3.65991 8.83 0.98877C4.66605 2.64689 5.63443 7.42763 5.5099 8.8794C4.46255 7.97408 4.26465 5.81096 4.26465 5.81096C3.15902 6.41179 2.60461 8.01638 2.60461 9.31794C2.60461 12.4654 5.02022 15.0168 7.99997 15.0168C10.9797 15.0168 13.3953 12.4654 13.3953 9.31794C13.3953 7.44747 12.0951 6.58464 12.1108 4.03674Z"
                    fill="white"
                />
            </svg>
            {{ formatNumber(item.likes) }}
        </div>

        <!-- 底部渐变蒙层 -->
        <div
            class="absolute bottom-0 left-0 right-0 h-20 z-[1] pointer-events-none"
            style="
                background: linear-gradient(
                    to top,
                    rgba(0, 0, 0, 0.7),
                    transparent
                );
            "
        ></div>

        <!-- 底部标题：默认显示，hover 时隐藏 -->
        <p
            v-if="item.title && showTitle"
            class="absolute bottom-4 left-1/2 -translate-x-1/2 z-[2] text-white text-sm font-medium truncate max-w-[90%] text-center opacity-100 group-hover:opacity-0 transition-opacity duration-300"
        >
            {{ item.title }}
        </p>

        <!-- Use This Template 按钮：hover 时显示 -->
        <button
            class="absolute bottom-4 left-1/2 -translate-x-1/2 z-[3] py-2 px-6 bg-[#99F768] text-[#000] text-sm font-normal leading-normal rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap"
            @click.stop="handleUseTemplate"
        >
            {{ t('menus.useThisTemplate') }}
        </button>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from '~/composables/useI18n'
import { getResourceThumbnail } from '~/utils/common'

interface EffectItem {
    id: string | number
    video?: string
    cover?: string
    title?: string
    likes?: number
}

const props = withDefaults(
    defineProps<{
        item: EffectItem
        width?: number
        height?: number
        showTitle?: boolean
        autoSize?: boolean // 自适应容器尺寸（移动端使用）
    }>(),
    {
        width: 240,
        height: 320,
        showTitle: true,
        autoSize: false,
    }
)

const emit = defineEmits<{
    click: [item: EffectItem]
    useTemplate: [item: EffectItem]
    mediaLoad: [item: EffectItem, actualWidth: number, actualHeight: number]
}>()

const { t } = useI18n()

const videoRef = ref<HTMLVideoElement | null>(null)

// poster 使用 video 的 thumbnail 或 cover 的 thumbnail
const posterSrc = computed(() => {
    if (props?.item?.video) {
        return getResourceThumbnail(props.item.video)
    }
    return props?.item?.cover ? getResourceThumbnail(props.item.cover) : ''
})

// 动态样式
const cardStyle = computed(() => {
    if (props.autoSize) {
        return {
            width: '100%',
            height: '100%',
        }
    }
    return {
        width: `${props.width}px`,
        height: `${props.height}px`,
    }
})

// 格式化数字
const formatNumber = (num: number): string => {
    if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K'
    }
    return num.toString()
}

// 视频播放控制（绑定在卡片容器上，避免 hover 按钮/蒙层导致 video mouseleave 抖动）
const warmUpOnce = (video: HTMLVideoElement) => {
    if ((video.dataset as any).weryWarm === '1') return
    ;(video.dataset as any).weryWarm = '1'
    try {
        // 只提升 preload，不主动调用 load()：
        // load() 可能导致媒体管线重置（poster/首帧闪一下）并触发额外 206 请求。
        video.preload = 'auto'
    } catch {
        // ignore
    }
}

let pauseTimer: ReturnType<typeof setTimeout> | null = null

const playPreview = async () => {
    const video = videoRef.value
    if (!video) return
    if (pauseTimer) {
        clearTimeout(pauseTimer)
        pauseTimer = null
    }
    warmUpOnce(video)
    if (!video.paused) return
    try {
        await video.play()
    } catch (error: any) {
        if (error?.name !== 'AbortError') {
            console.warn('视频播放失败:', error)
        }
    }
}

const pausePreview = () => {
    const video = videoRef.value
    if (!video) return
    // hover 快速抖动时不要立刻 pause（会 Abort 网络/解码，导致下一次 hover 又要等）
    if (pauseTimer) clearTimeout(pauseTimer)
    pauseTimer = setTimeout(() => {
        pauseTimer = null
        if (videoRef.value && !videoRef.value.paused) {
            videoRef.value.pause()
        }
    }, 250)
}

// 媒体加载完成，获取实际尺寸
const onMediaLoad = (event: Event) => {
    const target = event.target as HTMLImageElement | HTMLVideoElement
    if (!target) return

    const actualWidth =
        (target as HTMLVideoElement)?.videoWidth ||
        (target as HTMLImageElement).naturalWidth ||
        0
    const actualHeight =
        (target as HTMLVideoElement)?.videoHeight ||
        (target as HTMLImageElement).naturalHeight ||
        0

    if (actualWidth > 0 && actualHeight > 0) {
        emit('mediaLoad', props.item, actualWidth, actualHeight)
    }
}

// 点击卡片
const handleClick = () => {
    emit('click', props.item)
}

// 点击使用模板
const handleUseTemplate = () => {
    emit('useTemplate', props.item)
}
</script>

<style lang="less" scoped>
.effects-card {
    background: #1b1d25;
}
</style>
